import { useTranslation } from "react-i18next"

import type { SortDirection, SortOption } from "../hooks/use-file-picker-sorting"

import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface SortingDropdownProps {
  sortOption: SortOption
  sortDirection: SortDirection
  onSortChange: (option: SortOption) => void
  className?: string
}

export function SortingDropdown({
  sortOption,
  sortDirection,
  onSortChange,
  className,
}: SortingDropdownProps) {
  const { t } = useTranslation()

  const getSortLabel = (option: SortOption, direction: SortDirection) => {
    const directionIcon =
      direction === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />

    switch (option) {
      case "name":
        return (
          <div className="flex items-center gap-2">
            <span>{t("filepicker.sort.name", { defaultValue: "Name" })}</span>
            {directionIcon}
          </div>
        )
      case "lastModified":
        return (
          <div className="flex items-center gap-2">
            <span>{t("filepicker.sort.last_modified", { defaultValue: "Last Modified" })}</span>
            {directionIcon}
          </div>
        )
      case "type":
        return (
          <div className="flex items-center gap-2">
            <span>{t("filepicker.sort.type", { defaultValue: "Type" })}</span>
            {directionIcon}
          </div>
        )
      default:
        return (
          <div className="flex items-center gap-2">
            <ArrowUpDown className="size-3" />
            <span>{t("filepicker.sort.sort", { defaultValue: "Sort" })}</span>
          </div>
        )
    }
  }

  const handleSortOptionClick = (option: SortOption) => {
    onSortChange(option)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className={`h-8 w-auto min-w-[120px] justify-between px-3 text-xs font-normal ${className}`}
          size="sm"
          variant="outline"
        >
          {getSortLabel(sortOption, sortDirection)}
          <ChevronDown className="ml-2 size-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px]">
        <DropdownMenuItem onClick={() => handleSortOptionClick("name")}>
          <div className="flex w-full items-center justify-between">
            <span>{t("filepicker.sort.name", { defaultValue: "Name" })}</span>
            {sortOption === "name" &&
              (sortDirection === "asc" ? (
                <ArrowUp className="size-3" />
              ) : (
                <ArrowDown className="size-3" />
              ))}
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSortOptionClick("lastModified")}>
          <div className="flex w-full items-center justify-between">
            <span>{t("filepicker.sort.last_modified", { defaultValue: "Last Modified" })}</span>
            {sortOption === "lastModified" &&
              (sortDirection === "asc" ? (
                <ArrowUp className="size-3" />
              ) : (
                <ArrowDown className="size-3" />
              ))}
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSortOptionClick("type")}>
          <div className="flex w-full items-center justify-between">
            <span>{t("filepicker.sort.type", { defaultValue: "Type" })}</span>
            {sortOption === "type" &&
              (sortDirection === "asc" ? (
                <ArrowUp className="size-3" />
              ) : (
                <ArrowDown className="size-3" />
              ))}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
